import React, { useRef, useEffect, useCallback } from 'react';
import './ScrollHero.css';
import logoEmblem from '../assets/logo-emblem.svg';
import childrenPhoto from '../assets/children-community.jpg';

/* City dot positions in SVG viewBox coords — real US city locations */
const CITY_SVG_COORDS = [
  /* SEED CITIES (first 6 — emblem circles fly to these) */
  [215,445],[380,355],[580,345],[610,290],[810,490],[1010,280],
  /* West Coast */
  [233,280],[252,342],[268,395],[275,475],[210,420],
  /* Mountain West */
  [310,145],[350,175],[290,210],[345,260],[370,290],
  [410,335],[365,390],[310,350],[330,390],[270,235],[300,255],
  [350,430],[380,465],[400,500],[290,425],
  /* Great Plains */
  [460,260],[490,300],[445,315],[465,205],[505,215],
  [510,235],[530,270],[560,320],[600,360],
  [480,365],[505,395],[460,410],[545,440],[575,465],[555,490],
  /* Texas */
  [440,510],[470,560],[520,570],[500,530],[490,490],[530,530],
  /* Great Lakes */
  [640,230],[670,200],[690,245],[705,250],[740,280],[720,310],
  [640,310],[660,310],[685,340],[720,350],[740,370],[760,340],
  /* South */
  [720,410],[750,440],[780,415],[600,400],[635,420],[610,445],
  [680,460],[710,480],[745,500],[780,530],[750,555],[820,555],
  /* Mid-Atlantic */
  [800,370],[830,340],[860,380],[870,345],[900,365],[910,380],
  [905,425],[920,435],
  /* Northeast */
  [965,305],[980,285],[1000,325],[970,260],[990,245],
  [1030,275],[1050,260],[1040,245],[1065,195],[1080,170],
  [1020,230],[1008,315],[1000,340],[990,355],
];

const SVG_VB = { x: 174, y: 100, w: 959, h: 593 };

function svgToScreen(sx, sy, W, H) {
  const wrapW = Math.min(W * 0.82, 1100);
  const svgAspect = SVG_VB.w / SVG_VB.h;
  const wrapH = wrapW / svgAspect;
  const scale = Math.min(wrapW / SVG_VB.w, wrapH / SVG_VB.h);
  const wrapLeft = (W - wrapW) / 2;
  const wrapTop = (H - wrapH) / 2;
  const svgContentW = SVG_VB.w * scale;
  const svgContentH = SVG_VB.h * scale;
  const svgOffX = wrapLeft + (wrapW - svgContentW) / 2;
  const svgOffY = wrapTop + (wrapH - svgContentH) / 2;
  return {
    x: svgOffX + (sx - SVG_VB.x) * scale,
    y: svgOffY + (sy - SVG_VB.y) * scale,
  };
}

function easeInOutCubic(t) { return t < 0.5 ? 4*t*t*t : 1 - Math.pow(-2*t+2, 3)/2; }

function generateCommunityTargets(w, h) {
  const t = [];
  const cx = w / 2, groundY = h * 0.68, s = Math.min(w, h) / 650;
  const line = (x1,y1,x2,y2,n) => {
    for (let i = 0; i < n; i++) {
      const p = i / (n - 1 || 1);
      t.push({ x: x1+(x2-x1)*p+(Math.random()-0.5)*1.5*s, y: y1+(y2-y1)*p+(Math.random()-0.5)*1.5*s });
    }
  };
  const rect = (x,y,rw,rh,d) => {
    const n = Math.max(4, Math.floor(rw*rh*d/(500*s*s)));
    for (let i = 0; i < n; i++) t.push({ x: x+Math.random()*rw, y: y+Math.random()*rh });
  };
  const filledCircle = (cx2,cy2,r,n) => {
    for (let i = 0; i < n; i++) {
      const a = Math.random()*Math.PI*2, rr = Math.random()*r;
      t.push({ x: cx2+Math.cos(a)*rr, y: cy2+Math.sin(a)*rr });
    }
  };
  const circle = (cx2,cy2,r,n) => {
    for (let i = 0; i < n; i++) {
      const a = (i/n)*Math.PI*2;
      t.push({ x: cx2+Math.cos(a)*r, y: cy2+Math.sin(a)*r });
    }
  };

  const hy = groundY;
  /* Main house */
  rect(cx-70*s,hy-100*s,140*s,100*s,0.45);
  line(cx-85*s,hy-100*s,cx,hy-165*s,28);
  line(cx,hy-165*s,cx+85*s,hy-100*s,28);
  line(cx-85*s,hy-100*s,cx+85*s,hy-100*s,20);
  rect(cx-14*s,hy-55*s,28*s,55*s,0.6);
  rect(cx-55*s,hy-85*s,26*s,22*s,0.7);
  rect(cx+30*s,hy-85*s,26*s,22*s,0.7);
  rect(cx+45*s,hy-175*s,14*s,35*s,0.5);
  filledCircle(cx+52*s,hy-195*s,6*s,5);
  filledCircle(cx+48*s,hy-210*s,8*s,6);
  /* Left house */
  const lhx = cx - 220*s;
  rect(lhx-50*s,hy-75*s,100*s,75*s,0.35);
  line(lhx-60*s,hy-75*s,lhx,hy-125*s,18);
  line(lhx,hy-125*s,lhx+60*s,hy-75*s,18);
  rect(lhx-12*s,hy-42*s,24*s,42*s,0.5);
  rect(lhx-40*s,hy-62*s,20*s,18*s,0.6);
  rect(lhx+20*s,hy-62*s,20*s,18*s,0.6);
  /* Right community building */
  const rhx = cx + 220*s;
  rect(rhx-60*s,hy-90*s,120*s,90*s,0.4);
  line(rhx-65*s,hy-90*s,rhx,hy-115*s,16);
  line(rhx,hy-115*s,rhx+65*s,hy-90*s,16);
  rect(rhx-16*s,hy-50*s,32*s,50*s,0.5);
  rect(rhx-48*s,hy-78*s,18*s,16*s,0.5);
  rect(rhx+30*s,hy-78*s,18*s,16*s,0.5);
  /* Family */
  const p1x = cx-35*s, p1y = hy+25*s;
  circle(p1x,p1y-50*s,8*s,12); filledCircle(p1x,p1y-50*s,5*s,5);
  line(p1x,p1y-40*s,p1x,p1y-5*s,10);
  line(p1x-16*s,p1y-28*s,p1x+16*s,p1y-28*s,8);
  line(p1x,p1y-5*s,p1x-12*s,p1y+25*s,7);
  line(p1x,p1y-5*s,p1x+12*s,p1y+25*s,7);
  const p2x = cx+35*s, p2y = hy+25*s;
  circle(p2x,p2y-48*s,7*s,11); filledCircle(p2x,p2y-48*s,4*s,4);
  line(p2x,p2y-38*s,p2x,p2y-5*s,9);
  line(p2x-14*s,p2y-26*s,p2x+14*s,p2y-26*s,7);
  line(p2x,p2y-5*s,p2x-11*s,p2y+23*s,6);
  line(p2x,p2y-5*s,p2x+11*s,p2y+23*s,6);
  const ckx = cx, cky = hy+35*s;
  circle(ckx,cky-32*s,6*s,10); filledCircle(ckx,cky-32*s,4*s,4);
  line(ckx,cky-24*s,ckx,cky,7);
  line(ckx-12*s,cky-16*s,ckx+12*s,cky-16*s,5);
  line(ckx,cky,ckx-8*s,cky+18*s,5);
  line(ckx,cky,ckx+8*s,cky+18*s,5);
  line(p1x+16*s,p1y-28*s,ckx-12*s,cky-16*s,5);
  line(p2x-14*s,p2y-26*s,ckx+12*s,cky-16*s,5);
  /* Trees */
  const t1x = cx-320*s; line(t1x,hy,t1x,hy-60*s,9); filledCircle(t1x,hy-80*s,30*s,30);
  const t2x = cx-130*s; line(t2x,hy,t2x,hy-50*s,8); filledCircle(t2x,hy-68*s,24*s,22);
  const t3x = cx+130*s; line(t3x,hy,t3x,hy-55*s,8); filledCircle(t3x,hy-75*s,28*s,26);
  const t4x = cx+320*s; line(t4x,hy,t4x,hy-65*s,9); filledCircle(t4x,hy-88*s,32*s,32);
  /* Paths and ground */
  line(cx-8*s,hy,cx-14*s,hy+70*s,12);
  line(cx+8*s,hy,cx+14*s,hy+70*s,12);
  line(cx-380*s,hy,cx+380*s,hy,80);
  /* Fence */
  for (let fi = -350; fi <= 350; fi += 30) {
    if (Math.abs(fi) < 90 || Math.abs(fi-(-220)) < 55 || Math.abs(fi-220) < 65) continue;
    line(cx+fi*s,hy-2*s,cx+fi*s,hy-22*s,4);
  }
  line(cx-350*s,hy-18*s,cx-95*s,hy-18*s,25);
  line(cx+95*s,hy-18*s,cx+350*s,hy-18*s,25);
  /* Heart */
  const heartCx = cx, heartCy = hy-70*s;
  for (let hi = 0; hi < 20; hi++) {
    const ha = (hi/20)*Math.PI*2;
    const hpx = 10*s*16*Math.pow(Math.sin(ha),3)/16;
    const hpy = -10*s*(13*Math.cos(ha)-5*Math.cos(2*ha)-2*Math.cos(3*ha)-Math.cos(4*ha))/16;
    t.push({ x: heartCx+hpx, y: heartCy+hpy });
  }
  /* Sun */
  const sunX = cx+280*s, sunY = hy-240*s;
  filledCircle(sunX,sunY,18*s,15);
  for (let ri = 0; ri < 10; ri++) {
    const ra = (ri/10)*Math.PI*2;
    line(sunX+Math.cos(ra)*22*s,sunY+Math.sin(ra)*22*s,sunX+Math.cos(ra)*35*s,sunY+Math.sin(ra)*35*s,3);
  }
  return t;
}

/* ============================================================
   US MAP SVG PATHS — inline to avoid external fetch
   ============================================================ */
const US_MAP_PATHS = [
  "M929.4,545.5l2.3,7.3l3.7,9.7l5.3,9.4l3.7,6.3l4.8,5.5l4,3.7l1.6,2.9l-1.1,1.3L953,593l2.9,7.4 l2.9,2.9l2.6,5.3l3.6,5.8l4.5,8.2l1.3,7.6l0.5,12l0.6,1.8l-0.3,3.4l-2.4,1.3l0.3,1.9l-0.6,1.9l0.3,2.4l0.5,1.9l-2.7,3.2l-3.1,1.5 l-3.9,0.2l-1.5,1.6l-2.4,1l-1.3-0.5l-1.1-1l-0.3-2.9l-0.8-3.4l-3.4-5.2l-3.6-2.3l-3.9-0.3l-0.8,1.3l-3.1-4.4l-0.6-3.6l-2.6-4 l-1.8-1.1l-1.6,2.1l-1.8-0.3l-2.1-5l-2.9-3.9l-2.9-5.3l-2.6-3.1l-3.6-3.7l2.1-2.4l3.2-5.5l-0.2-1.6l-4.5-1l-1.6,0.6l0.3,0.6l2.6,1 l-1.5,4.5l-0.8,0.5l-1.8-4l-1.3-4.8l-0.3-2.7l1.5-4.7v-9.5L910,585l-1.3-3.1l-5.2-1.3l-1.9-0.6l-1.6-2.6l-3.4-1.6l-1.1-3.4l-2.7-1 l-2.4-3.7l-4.2-1.5l-2.9-1.5h-2.6l-4,0.8l-0.2,1.9l0.8,1l-0.5,1.1l-3.1-0.2l-3.7,3.6l-3.6,1.9h-3.9l-3.2,1.3l-0.3-2.7l-1.6-1.9 l-2.9-1.1l-1.6-1.5l-8.1-3.9l-7.6-1.8l-4.4,0.6l-6,0.5l-6,2.1l-3.5,0.6l-0.2-8l-2.6-1.9l-1.8-1.8l0.3-3.1l10.2-1.3l25.5-2.9 l6.8-0.6l5.4,0.3l2.6,3.9l1.5,1.5l8.1,0.5l10.8-0.6l21.5-1.3l5.4-0.7l4.6,0l0.2,2.9l3.8,0.8l0.3-4.8l-1.6-4.5l1-0.7l5.1,0.5 L929.4,545.5z M941.9,677.9l2.4-0.6l1.3-0.2l1.5-2.3l2.3-1.6l1.3,0.5l1.7,0.3l0.4,1.1l-3.5,1.2l-4.2,1.5l-2.3,1.2L941.9,677.9z M955.4,672.9l1.2,1.1l2.7-2.1l5.3-4.2l3.7-3.9l2.5-6.6l1-1.7l0.2-3.4l-0.7,0.5l-1,2.8l-1.5,4.6l-3.2,5.3l-4.4,4.2l-3.4,1.9 L955.4,672.9z",
  "M935.2,512.9l-1.8,1l-2.6-1.3l-0.6-2.1l-1.3-3.6l-2.3-2.1l-2.6-0.6l-1.6-4.8l-2.7-6l-4.2-1.9 l-2.1-1.9l-1.3-2.6L910,485l-2.3-1.3l-2.3-2.9l-3.1-2.3l-4.5-1.8l-0.5-1.5l-2.4-2.9l-0.5-1.5l-3.4-5.2l-3.4,0.2l-4-2.4l-1.3-1.3 l-0.3-1.8l0.8-1.9l2.3-1l-0.3-2.1l6.1-2.6l9.1-4.5l7.3-0.8l16.5-0.5l2.3,1.9l1.6,3.2l4.4-0.5l12.6-1.5l2.9,0.8l12.6,7.6l10.1,8.1 l-5.4,5.5l-2.6,6.1l-0.5,6.3l-1.6,0.8l-1.1,2.7l-2.4,0.6l-2.1,3.6l-2.7,2.7l-2.3,3.4l-1.6,0.8l-3.6,3.4l-2.9,0.2l1,3.2l-5,5.5 L935.2,512.9z",
  "M863.6,458l-4.8,0.8l-8.4,1.1l-8.6,0.9v2.2l0.2,2.1l0.6,3.4l3.4,7.9l2.4,9.9l1.5,6.1l1.6,4.8l1.5,7 l2.1,6.3l2.6,3.4l0.5,3.4l1.9,0.8l0.2,2.1l-1.8,4.8l-0.5,3.2l-0.2,1.9l1.6,4.4l0.3,5.3l-0.8,2.4l0.6,0.8l1.5,0.8l0.6,3.4l2.6,3.9 l1.5,1.5l7.9,0.2l10.8-0.6l21.5-1.3l5.4-0.7l4.6,0l0.2,2.9l2.6,0.8l0.3-4.4l-1.6-4.5l1.1-1.6l5.8,0.8l5,0.3l-0.8-6.3l2.3-10 l1.5-4.2l-0.5-2.6l3.3-6.2l-0.5-1.4l-1.9,0.7l-2.6-1.3l-0.6-2.1l-1.3-3.6l-2.3-2.1l-2.6-0.6l-1.6-4.8l-2.9-6.3l-4.2-1.9l-2.1-1.9 l-1.3-2.6l-2.1-1.9l-2.3-1.3l-2.3-2.9l-3.1-2.3l-4.5-1.8l-0.5-1.5l-2.4-2.9l-0.5-1.5l-3.4-4.9l-3.4,0.2l-4.1-3l-1.3-1.3l-0.3-1.8 l0.8-1.9l2.4-1.2l-1.1-1.2l0.1-0.3l-5.8,1l-7,0.8L863.6,458z",
  "M799.6,566.8l-1.6-15.2l-2.7-18.8l0.2-14.1l0.8-31l-0.2-16.7l0.2-6.4l7.8-0.4l27.8-2.6l8.9-0.7 l-0.1,2.2l0.2,2.1l0.6,3.4l3.4,7.9l2.4,9.9l1.5,6.1l1.6,4.8l1.5,7l2.1,6.3l2.6,3.4l0.5,3.4l1.9,0.8l0.2,2.1l-1.8,4.8l-0.5,3.2 l-0.2,1.9l1.6,4.4l0.3,5.3l-0.8,2.4l0.6,0.8l1.5,0.8l1,2.5h-6.3l-6.8,0.6l-25.5,2.9l-10.4,1.4l-0.1,3.8l1.8,1.8l2.6,1.9l0.6,7.9 l-5.5,2.6l-2.7-0.3l2.7-1.9v-1l-3.1-6l-2.3-0.6l-1.5,4.4l-1.3,2.7l-0.6-0.2H799.6z",
];

const ScrollHero = () => {
  const heroRef = useRef(null);
  const canvasRef = useRef(null);
  const mapWrapRef = useRef(null);
  const emblemRef = useRef(null);
  const photoRef = useRef(null);
  
  // Animation state
  const scrollProgressRef = useRef(0);
  const timeRef = useRef(0);
  const animationIdRef = useRef(null);
  const particlesRef = useRef([]);
  const ctxRef = useRef(null);
  const dimensionsRef = useRef({ width: 0, height: 0 });

  // Initialize particles and canvas
  const initializeParticles = useCallback(() => {
    const hero = heroRef.current;
    const canvas = canvasRef.current;
    if (!hero || !canvas) return;

    const rect = hero.getBoundingClientRect();
    const W = rect.width;
    const H = rect.height;
    
    canvas.width = W;
    canvas.height = H;
    dimensionsRef.current = { width: W, height: H };
    
    const ctx = canvas.getContext('2d');
    ctxRef.current = ctx;

    // Initialize particles from city coordinates
    const particles = [];
    
    CITY_SVG_COORDS.forEach((coord, i) => {
      const screenPos = svgToScreen(coord[0], coord[1], W, H);
      const scatterRadius = Math.min(W, H) * 0.4;
      const scatterAngle = Math.random() * Math.PI * 2;
      const scatterDist = Math.random() * scatterRadius;
      
      const communityTargets = generateCommunityTargets(W, H);
      const targetIndex = Math.floor(Math.random() * communityTargets.length);
      const communityTarget = communityTargets[targetIndex];
      
      particles.push({
        // Original map position
        mapX: screenPos.x,
        mapY: screenPos.y,
        
        // Scattered position
        scatterX: W/2 + Math.cos(scatterAngle) * scatterDist,
        scatterY: H/2 + Math.sin(scatterAngle) * scatterDist,
        
        // Community formation position  
        communityX: communityTarget.x,
        communityY: communityTarget.y,
        
        // Current position
        x: screenPos.x,
        y: screenPos.y,
        
        // Visual properties
        size: i < 6 ? 4 + Math.random() * 2 : 2 + Math.random() * 3, // Seed cities larger
        opacity: 0.4 + Math.random() * 0.6,
        isOrange: i < 6 || Math.random() < 0.15, // Seed cities + 15% others are orange
        drift: Math.random() * Math.PI * 2
      });
    });
    
    particlesRef.current = particles;
  }, []);

  // Update scroll progress
  const updateScrollProgress = useCallback(() => {
    const hero = heroRef.current;
    if (!hero) return;
    
    const rect = hero.getBoundingClientRect();
    const scrollable = hero.offsetHeight - window.innerHeight;
    const progress = Math.max(0, Math.min(1, -rect.top / scrollable));
    
    scrollProgressRef.current = progress;
  }, []);

  // Update UI elements based on scroll
  const updateUI = useCallback(() => {
    const progress = scrollProgressRef.current;
    
    // Map visibility
    if (mapWrapRef.current) {
      let mapOpacity = 0;
      if (progress > 0.03) {
        const act1 = Math.max(0, Math.min(1, (progress - 0.03) / 0.27));
        mapOpacity = Math.min(1, act1 * 2);
        
        if (progress > 0.30) {
          const act2 = Math.max(0, Math.min(1, (progress - 0.30) / 0.14));
          mapOpacity = Math.max(0, 1 - act2 * 2.5);
        }
      }
      mapWrapRef.current.style.opacity = mapOpacity;
    }

    // Emblem visibility
    if (emblemRef.current) {
      const emblemOut = Math.max(0, Math.min(1, progress / 0.04));
      emblemRef.current.style.opacity = 1 - emblemOut;
      emblemRef.current.style.transform = `scale(${1 - emblemOut * 0.15})`;
      emblemRef.current.style.visibility = emblemOut >= 1 ? 'hidden' : 'visible';
    }

    // Photo visibility
    if (photoRef.current) {
      const photoPhase = Math.max(0, Math.min(1, (progress - 0.50) / 0.25));
      const photoExitPhase = Math.max(0, Math.min(1, (progress - 0.85) / 0.10));
      const photoScrollY = -photoExitPhase * 110;
      const photoFade = 1 - photoExitPhase;
      
      photoRef.current.style.opacity = photoPhase * photoFade;
      photoRef.current.style.transform = `translateY(${photoScrollY}%)`;
    }

    // Text phase updates would go here for other elements
    
  }, []);

  // Main render loop
  const render = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    const particles = particlesRef.current;
    const progress = scrollProgressRef.current;
    
    if (!canvas || !ctx || !particles.length) {
      animationIdRef.current = requestAnimationFrame(render);
      return;
    }

    timeRef.current += 0.008;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Animation phases
    const act2 = Math.max(0, Math.min(1, (progress - 0.30) / 0.14));
    const act3 = Math.max(0, Math.min(1, (progress - 0.44) / 0.18));
    const act3ease = easeInOutCubic(act3);

    // Update and render particles
    const morphT = act3ease;
    const time = timeRef.current;
    
    particles.forEach(p => {
      // Calculate drift for organic movement
      const driftX = Math.sin(time * 0.35 + p.drift) * 15 * (1 - morphT);
      const driftY = Math.cos(time * 0.25 + p.drift) * 10 * (1 - morphT);
      
      // Position transitions
      if (act3 <= 0) {
        // Map to scatter transition
        p.x = p.mapX + (p.scatterX - p.mapX) * act2 + driftX;
        p.y = p.mapY + (p.scatterY - p.mapY) * act2 + driftY;
      } else {
        // Scatter to community transition
        p.x = p.scatterX + (p.communityX - p.scatterX) * morphT + driftX;
        p.y = p.scatterY + (p.communityY - p.scatterY) * morphT + driftY;
      }
      
      // Visual properties
      const alpha = p.opacity * (0.2 + act2 * 0.8) * (act2 < 0.3 ? 0.3 + act2 / 0.3 * 0.7 : 1);
      const size = p.size * (1 + (1 - morphT) * 0.3);
      
      // Draw particle
      ctx.fillStyle = p.isOrange 
        ? `rgba(233,118,43,${alpha})` 
        : `rgba(255,255,255,${alpha * 0.5})`;
      
      ctx.beginPath();
      ctx.arc(p.x, p.y, size, 0, Math.PI * 2);
      ctx.fill();
    });

    // Warm glow during community phase
    if (morphT > 0.5) {
      const glowAlpha = (morphT - 0.5) * 0.1;
      const gradient = ctx.createRadialGradient(
        canvas.width / 2, canvas.height * 0.55, 0,
        canvas.width / 2, canvas.height * 0.55, canvas.width * 0.3
      );
      gradient.addColorStop(0, `rgba(233,118,43,${glowAlpha})`);
      gradient.addColorStop(1, 'rgba(233,118,43,0)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    animationIdRef.current = requestAnimationFrame(render);
  }, []);

  // Event handlers
  const handleScroll = useCallback(() => {
    updateScrollProgress();
    updateUI();
  }, [updateScrollProgress, updateUI]);

  const handleResize = useCallback(() => {
    initializeParticles();
  }, [initializeParticles]);

  // Effects
  useEffect(() => {
    initializeParticles();
    updateScrollProgress();
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);
    
    // Start render loop
    animationIdRef.current = requestAnimationFrame(render);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
    };
  }, [handleScroll, handleResize, render, initializeParticles, updateScrollProgress]);

  const progress = scrollProgressRef.current;
  const textPhase = Math.max(0, Math.min(1, (progress - 0.70) / 0.25));

  return (
    <section className="scroll-hero" ref={heroRef}>
      <div className="scroll-hero__sticky">
        {/* Canvas for particle animation */}
        <canvas 
          ref={canvasRef}
          className="scroll-hero__canvas"
          style={{ opacity: Math.max(0, 1 - textPhase * 1.5) }}
        />
        
        {/* US Map SVG */}
        <div ref={mapWrapRef} className="scroll-hero__map">
          <svg viewBox="174 100 959 593" className="us-map">
            {US_MAP_PATHS.map((path, i) => (
              <path key={i} d={path} fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.3)" strokeWidth="1"/>
            ))}
          </svg>
        </div>

        {/* Company Emblem */}
        <div ref={emblemRef} className="scroll-hero__emblem">
          <img src={logoEmblem} alt="Clarity Impact Finance" />
        </div>

        {/* Background Photo */}
        <div ref={photoRef} className="scroll-hero__photo">
          <img src={childrenPhoto} alt="Community strengthened" />
          <div className="scroll-hero__photo-overlay"></div>
        </div>

        {/* Text Overlays */}
        <div className="scroll-hero__overlay">
          <div className="scroll-hero__pre-text">
            From Capital to Community
          </div>
          <h1>
            <span className="scroll-hero__line scroll-hero__line--orange">
              Strengthening
            </span>
            <span className="scroll-hero__line scroll-hero__line--white">
              Communities
            </span>
          </h1>
          <div className="scroll-hero__divider"></div>
          <p className="scroll-hero__subtitle">
            Through strategic capital deployment and innovative financial solutions, 
            we help mission-driven lenders create lasting impact in underserved communities.
          </p>
        </div>

        {/* Early Tagline */}
        <div className="scroll-hero__tagline">
          Capital flows, communities grow
        </div>

        {/* Scroll Hint */}
        <div className="scroll-hero__hint">
          Scroll to explore →
        </div>
      </div>
    </section>
  );
};

export default ScrollHero;
